// "Point at it on the page" — the annotation half of the header's Bug / Improve button
// (Jacky, 12 October 2026: "input text and annotate what is needed to fix/improve").
//
// A report that says "the date is wrong" is only useful with WHICH date. Instead of a screenshot
// (heavy, and a picture of a number is not the number), he clicks the element: we record what a
// person would call it (its label or the first words on it), a short DOM path that finds it again,
// and where it sat on screen. Pure functions over a minimal element shape, so they test under
// `node --test` without a DOM.
export type Annotation = { label: string; path: string; text: string; rect?: { x: number; y: number; w: number; h: number } };

/** The slice of `Element` these functions read. */
export interface ElementLike {
  tagName: string;
  id?: string;
  parentElement?: ElementLike | null;
  children?: ArrayLike<ElementLike>;
  textContent?: string | null;
  getAttribute?(name: string): string | null;
  getBoundingClientRect?(): { x: number; y: number; width: number; height: number };
}

const squash = (s: string | null | undefined, n: number): string => {
  const t = (s ?? '').replace(/\s+/g, ' ').trim();
  return t.length > n ? `${t.slice(0, n - 1)}…` : t;
};
const attr = (el: ElementLike, name: string) => el.getAttribute?.(name) ?? null;

/** Tags that carry no meaning of their own: the picker climbs out of them to the thing he meant. */
const WRAPPERS = new Set(['SPAN', 'SVG', 'PATH', 'B', 'I', 'EM', 'STRONG', 'SMALL', 'KBD', 'CODE']);

/** The element a click on `el` most plausibly meant: a control or landmark it sits in, else itself. */
export function targetOf(el: ElementLike): ElementLike {
  let cur: ElementLike | null = el;
  for (let hops = 0; cur && hops < 4; hops++) {
    const tag = cur.tagName.toUpperCase();
    if (['A', 'BUTTON', 'INPUT', 'SELECT', 'TEXTAREA', 'LABEL', 'TD', 'TH', 'LI', 'SUMMARY'].includes(tag) || attr(cur, 'role')) return cur;
    if (!WRAPPERS.has(tag)) return cur;
    cur = cur.parentElement ?? null;
  }
  return el;
}

/** What a person would call it: aria-label, title, placeholder, then its first words, then its tag. */
export function labelOf(el: ElementLike): string {
  const tag = el.tagName.toLowerCase();
  const named = attr(el, 'aria-label') || attr(el, 'title') || attr(el, 'placeholder') || attr(el, 'alt');
  if (named) return squash(named, 80);
  const words = squash(el.textContent, 60);
  if (words) return words;
  const value = attr(el, 'value') || attr(el, 'name');
  if (value) return `${tag} ${squash(value, 40)}`;
  return tag;
}

/** A short path that finds it again: `main > section:nth-of-type(2) > button`, ids and test ids when present. */
export function pathOf(el: ElementLike, maxDepth = 6): string {
  const parts: string[] = [];
  let cur: ElementLike | null = el;
  while (cur && parts.length < maxDepth) {
    const tag = cur.tagName.toLowerCase();
    if (tag === 'html' || tag === 'body') break;
    const testId = attr(cur, 'data-testid');
    if (cur.id) { parts.unshift(`#${cur.id}`); break; }
    if (testId) { parts.unshift(`[data-testid="${testId}"]`); break; }
    let part = tag;
    const parent: ElementLike | null = cur.parentElement ?? null;
    if (parent?.children) {
      const siblings = Array.from(parent.children as ArrayLike<ElementLike>).filter((c) => c.tagName === cur!.tagName);
      if (siblings.length > 1) part += `:nth-of-type(${siblings.indexOf(cur) + 1})`;
    }
    parts.unshift(part);
    cur = parent ?? null;
  }
  return parts.join(' > ');
}

/** The annotation for a click on `el`. */
export function annotate(el: ElementLike): Annotation {
  const target = targetOf(el);
  const r = target.getBoundingClientRect?.();
  return {
    label: labelOf(target),
    path: pathOf(target),
    text: squash(target.textContent, 120),
    ...(r ? { rect: { x: Math.round(r.x), y: Math.round(r.y), w: Math.round(r.width), h: Math.round(r.height) } } : {}),
  };
}

/** What is captured for him, never typed. */
export function pageContext(w: { innerWidth: number; innerHeight: number; navigator?: { userAgent?: string; language?: string } }, extra: Record<string, string | number | boolean | null> = {}): Record<string, string | number | boolean | null> {
  return { viewport: `${w.innerWidth}x${w.innerHeight}`, user_agent: squash(w.navigator?.userAgent, 200) || null, ...extra };
}
